// src/hooks/useChapterState.ts
import React, { useState, useEffect, useCallback } from 'react';
import { Chapter, ChapterDetailEdits, ChapterVitals, MainView } from '../types.js';
import { initialChapterVitals } from '../constants.js';

export interface ChapterState {
    activeChapterDetailEdits: Partial<ChapterDetailEdits> | null;
    setActiveChapterDetailEdits: React.Dispatch<React.SetStateAction<Partial<ChapterDetailEdits> | null>>;
    revisionInput: string;
    setRevisionInput: React.Dispatch<React.SetStateAction<string>>;
    revisionWordCount: string;
    setRevisionWordCount: React.Dispatch<React.SetStateAction<string>>;
    handleDetailInputChange: (field: keyof Omit<ChapterDetailEdits, 'vitals'>, value: string) => void;
    handleVitalsInputChange: (vitalField: keyof ChapterVitals, value: string) => void;
    areChapterDetailsCurrentlyUnsaved: () => boolean;
}

export const useChapterState = ( // Added a comment to force re-evaluation
    activeChapter: Chapter | undefined,
    mainView: MainView,
    isInitialBlueprintReady: boolean
): ChapterState => {
    const [activeChapterDetailEdits, setActiveChapterDetailEdits] = useState<Partial<ChapterDetailEdits> | null>(null);
    const [revisionInput, setRevisionInput] = useState('');
    const [revisionWordCount, setRevisionWordCount] = useState('500');

    useEffect(() => {
        if (activeChapter) {
            const initialDetailEdits = {
                title: activeChapter.title || '',
                summary: activeChapter.summary || '',
                geography: activeChapter.geography || '',
                charactersInvolved: activeChapter.charactersInvolved || '',
                characterActions: activeChapter.characterActions || '',
                soulOfChapter: activeChapter.soulOfChapter || '',
                expectedWordCount: activeChapter.expectedWordCount?.toString() || '',
                vitals: activeChapter.vitals ? { ...activeChapter.vitals } : { ...initialChapterVitals },
                content: '', // Initialize as empty, will be populated by loadContent effect
            };

            // For now, let's remove the duplicate API call from here
            // The content loading should be handled by useAppEffects
            
            setActiveChapterDetailEdits(initialDetailEdits);
        } else {
            setActiveChapterDetailEdits(null);
        }
    }, [activeChapter]);

    const handleDetailInputChange = (field: keyof Omit<ChapterDetailEdits, 'vitals'>, value: string) => {
        setActiveChapterDetailEdits((prev: Partial<ChapterDetailEdits> | null) => {
            if (!prev) return null;
            return { ...prev, [field]: value };
        });
    };

    const handleVitalsInputChange = (vitalField: keyof ChapterVitals, value: string) => {
        setActiveChapterDetailEdits((prev: Partial<ChapterDetailEdits> | null) => {
            if (!prev) return null;
            return { ...prev, vitals: { ...(prev.vitals || initialChapterVitals), [vitalField]: value } };
        });
    };

    const areChapterDetailsCurrentlyUnsaved = useCallback((): boolean => {
        if (!activeChapter || !activeChapterDetailEdits) return false;
        
        // Helper function to normalize values (treat undefined, null, and empty string as equivalent)
        const normalize = (value: any): string => (value || '').toString().trim();
        
        // Check main fields
        if (normalize(activeChapterDetailEdits.title) !== normalize(activeChapter.title)) return true;
        if (normalize(activeChapterDetailEdits.summary) !== normalize(activeChapter.summary)) return true;
        if (normalize(activeChapterDetailEdits.geography) !== normalize(activeChapter.geography)) return true;
        if (normalize(activeChapterDetailEdits.charactersInvolved) !== normalize(activeChapter.charactersInvolved)) return true;
        if (normalize(activeChapterDetailEdits.characterActions) !== normalize(activeChapter.characterActions)) return true;
        if (normalize(activeChapterDetailEdits.soulOfChapter) !== normalize(activeChapter.soulOfChapter)) return true;
        if (Number(activeChapterDetailEdits.expectedWordCount || 0) !== Number(activeChapter.expectedWordCount || 0)) return true;
        
        // Check vitals with normalization
        const editedVitals = activeChapterDetailEdits.vitals || initialChapterVitals;
        const originalVitals = activeChapter.vitals || initialChapterVitals;
        for (const key in editedVitals) {
            if (normalize(editedVitals[key as keyof ChapterVitals]) !== normalize(originalVitals[key as keyof ChapterVitals])) return true;
        }
        for (const key in originalVitals) {
            if (!(key in editedVitals) && normalize(originalVitals[key as keyof ChapterVitals]) !== '') return true;
        }
        return false;
    }, [activeChapter, activeChapterDetailEdits]);

    return {
        activeChapterDetailEdits, setActiveChapterDetailEdits,
        revisionInput, setRevisionInput,
        revisionWordCount, setRevisionWordCount,
        handleDetailInputChange,
        handleVitalsInputChange,
        areChapterDetailsCurrentlyUnsaved,
    };
};